

import React, { useEffect, useMemo, useState, useContext, cloneElement, Children } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { AuthContext } from "../login/AuthContext";
import translations from "../../utils/language/translations.json";
import { useTranslation } from "react-i18next";
import bellicon from "../../iconosv0/src/icons/bell.svg";

export default function NotificationIcon({
  count,           // optional external count (e.g., from /summary). If omitted, we use server unread_total
  asChild = false, // if true, use single child element as trigger
  children,
}) {
  const { userId, liveCsrfToken, loading, jwtToken, isAuthenticated } = useContext(AuthContext);
  const { i18n } = useTranslation();
  const t = (key) => translations.Notification?.[key]?.[i18n.language] ?? key;

  const navigate = useNavigate();
  const location = useLocation();

  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);       // notifications
  const [cursor, setCursor] = useState(null);   // { beforeId, beforeTime }
  const [badge, setBadge] = useState(Number(count || 0));

  // keep badge synced with external prop when provided
  useEffect(() => {
    console.log('count',count)
    if (Number.isFinite(Number(count))) setBadge(Number(count));
  }, [count]);

  const open = Boolean(anchorEl);

  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    "x-csrf-token": liveCsrfToken,
    "Authorization": `Bearer ${jwtToken}`,
  }), [liveCsrfToken, jwtToken]);

  async function loadList(next) {
    if (!userId) return;
    const url = new URL(`${process.env.REACT_APP_API_BASE}/api/notifications/list`);
    url.searchParams.set("limit", "20");
    if (next?.beforeId && next?.beforeTime) {
      url.searchParams.set("beforeId", String(next.beforeId));
      url.searchParams.set("beforeTime", new Date(next.beforeTime).toISOString());
    }
    const res = await fetch(url, { credentials: "include", headers });
    
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    console.log('data.items',data.items)
    setItems((prev) => (next ? [...prev, ...(data.items || [])] : (data.items || [])));
    setCursor(data.nextCursor || null);
     
    // If parent didn't pass a count, use server unread_total
    if (!Number.isFinite(Number(count))) setBadge(Number(data.unread_total || 0));
  }

  // useEffect(() => {
  //   if (loading || !userId || !isAuthenticated || !liveCsrfToken) return;
  //   loadList(null).catch((e) => console.error("notifications list error:", e));
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [loading, isAuthenticated, userId, liveCsrfToken]);

  // Helpers
  const parseMeta = (m) => {
    if (!m) return {};
    try { return typeof m === "string" ? JSON.parse(m) : m; } catch { return {}; }
  };

  async function markRead(id) {
    try {
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: 1 } : n)));
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/notifications/read`,
        {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify({ ids: [id] }),
        }
      );
      const data = await res.json();
      if (!data.success) throw new Error("read failed");
      if (!Number.isFinite(Number(count))) setBadge(Number(data.unread_total || 0));
    } catch (e) {
      console.error("markRead error:", e);
    }
  }

  async function deleteOne(id) {
    try {
      setItems((prev) => prev.filter((n) => n.id !== id));
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE}/api/notifications/delete`,
        {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify({ id }),
        }
      );
      const data = await res.json();
      if (!data.success) throw new Error("delete failed");
      if (!Number.isFinite(Number(count))) setBadge(Number(data.unread_total || 0));
    } catch (e) {
      console.error("delete error:", e);
    }
  }

// NEW: actions that are not "readable"
const [actions, setActions] = useState([]); // [{kind, id, role, status, cta, updatedAt, href}]

// loader (auth headers already built as `headers`)
async function loadActions() {
  if (!userId) return;
  const res = await fetch(
    `${process.env.REACT_APP_API_BASE}/api/actions`,
    { credentials: "include", headers }
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json(); // array
  console.log('ctaaction',data)
  setActions(Array.isArray(data) ? data : []);
}
useEffect(() => {
  if (loading || !userId || !isAuthenticated || !liveCsrfToken) return;
  loadList(null).catch((e) => console.error("notifications list error:", e));
  loadActions().catch((e) => console.error("actions list error:", e));   // <-- NEW
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [loading, isAuthenticated, userId, liveCsrfToken]);


  function openModalFromMeta(meta) {
    // Optional global modal hook — only triggers if your app exposes it.
    // We keep this super lightweight & non-breaking.
    try {
      if (meta?.modal && typeof window?.openAppModal === "function") {
        window.openAppModal(meta.modal.kind, meta.modal.props || {});
        return true;
      }
    } catch (e) {
      console.warn("openModalFromMeta error:", e);
    }
    return false;
  }
const normalizeOptionValue = (value, role) => {
  const v = String(value || "").trim();

  // Shipment group
  if (v === "AwaitingShipment" || v === "WaitingShipment") {
    return role === "Buyer" ? "ComingYourWay" : "ShipNow";
  }
  if (v === "Shipped") {
    return role === "Buyer" ? "ComingYourWay" : "Shipped";
  }

  // Payment group
  if (v === "AwaitingPayment" || v === "WaitingPayment") {
    return role === "Buyer" ? "PayNow" : "WaitingPayment";
  }
   console.log('ici v')
  // Otherwise leave as-is (Waiting, Accepted, Rejected, Completed, Sent, Countered, etc.)
  return v;
};
// --- helpers ---------------------------------------------------------

// Map whatever you store in meta/modal/status → the tab your UI expects
// function mapToPreselected({ status_to, modalPre, asWho }) {
//   // Prefer the explicit modal prop if present, else fallback to backend status
//   const key = (modalPre || status_to || "").toString();

//   // Normalize both sets of names to your working tabs
//   switch (key) {
//     case "PayNow":            return "WaitingPayment"; // your page expects this tab name
//     case "AwaitingPayment":   return "WaitingPayment";
//     case "WaitingPayment":    return "WaitingPayment";

//     case "ShipNow":           return "WaitingShipment";
//     case "ComingYourWay":     return "WaitingShipment";
//     case "WaitingShipment":   return "WaitingShipment";

//     case "Rejected":          return "Rejected";
//     case "Accepted":          return "Accepted";
//     case "Shipped":           return "Shipped";
//     case "Completed":         return "Completed";

//     case "Waiting":
//     default:                  return "Waiting";
//   }
  
//  normalizeOptionValue(key,asWho)
// }

// ---- Role-aware normalization ---------------------------------------------
 const normalizeStatusByRole = (value, role) => {
  const v = String(value ?? "").trim();
  const lc = v.toLowerCase();
  const r = role === "Buyer" ? "Buyer" : "Seller";
  console.log('icilc',value,' role ' ,role)
  // Payment
  if (lc === "awaitingpayment" || lc === "waitingpayment" || lc === "paynow") {
    console.log('icilc 123',lc)
    return r === "Buyer" ? "PayNow" : "WaitingPayment";
  }

  // Shipment
  if (lc === "awaitingshipment" || lc === "waitingshipment") {
    return r === "Buyer" ? "ComingYourWay" : "ShipNow";
  }

  if (lc === "shipped") {
    return r === "Buyer" ? "ComingYourWay" : "Shipped";
  }

  // passthroughs (Accepted, Rejected, Waiting, Completed, Sent, Countered, etc.)
  return v || "Waiting";
};

// ---- Tab mapping (UI expects these keys) -----------------------------------
const ACTION_TO_TAB = {
  // Payment
  PayNow: "WaitingPayment",
  WaitingPayment: "WaitingPayment",

  // Shipment
  ShipNow: "WaitingShipment",
  ComingYourWay: "WaitingShipment",
  WaitingShipment: "WaitingShipment",

  // Others (direct)
  Rejected: "Rejected",
  Accepted: "Accepted",
  Shipped: "Shipped",
  Completed: "Completed",
  Waiting: "Waiting",
  Sent: "Waiting",
  Countered: "Waiting",
};

// Map backend/meta -> tab (role-aware)
 function mapToPreselected({ status_to, modalPre, asWho }) {
  const raw = String(modalPre ?? status_to ?? "").trim();
  const norm = normalizeStatusByRole(raw, asWho);
  //return ACTION_TO_TAB[norm] || "Waiting";
  return norm
}

// ---- URL builder -----------------------------------------------------------
function buildOffersPath({ preselected, transactionId, asWho } = {}) {
  const params = new URLSearchParams();
  params.set("sellView", "soldOrOffer");
  if (preselected)    params.set("preselected", preselected);
  if (transactionId)  params.set("transactionId", String(transactionId));
  // if (asWho)       params.set("asWho", asWho); // enable when your page reads it
  return `/marketplace/offers?${params.toString()}`;
}


// --- main handler ----------------------------------------------------

function goTo(n) {
  try {
    if (!n.isRead) markRead(n.id);

    // Pull what we can from the notification we already have
    const modalKind   = n?.meta?.modal?.kind;
    const modalProps  = n?.meta?.modal?.props || {};
    const asWho       = modalProps.asWho ?? n.asWho; // ok if undefined; we don't use it yet
    const modalPre    = modalProps.preselected;
    const transactionId = modalProps.transactionId ?? n.transactionId;

    // Decide the tab to open
    const preselected = mapToPreselected({
      status_to: n.status_to,
      modalPre,
      asWho
    });

    // Build the canonical path YOUR page understands today
    const finalPath = buildOffersPath({ preselected,transactionId });


    console.log('finalPath',finalPath)
    ///marketplace/offers?sellView=soldOrOffer&preselected=WaitingShipment
    ///marketplace/offers?sellView=soldOrOffer&preselected=Rejected
    // Same-page “refresh” trick so effects re-run if needed
    const current = location.pathname + location.search;
    if (current === finalPath) {
      navigate("/refresh", { replace: true });
      setTimeout(() => navigate(finalPath), 0);
    } else {
      navigate(finalPath);
    }

    setAnchorEl(null);
  } catch (e) {
    console.error("navigate error:", e);
  }
}



  const empty = items.length === 0;
  const effectiveCount = Number.isFinite(Number(count)) ? Number(count) : Number(badge || 0);
   
  // Trigger element (either child or default bell)
  const trigger = asChild
    ? cloneElement(Children.only(children), {
        onClick: (e) => {
          children.props.onClick?.(e);
          setAnchorEl(e.currentTarget);
          
        },
      })
    : (
      <span
        className="notif-trigger"
        onClick={(e) => {setAnchorEl(e.currentTarget);e.stopPropagation()}}
        aria-label={t("Notifications")}
      >
        <img src={bellicon} width="25" height="25" alt="" />
        {effectiveCount > 0 && <span className="badge" aria-label={String(effectiveCount)}>{effectiveCount}</span>}
      </span>
    );

  return (
    <>
      {trigger}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={(e) => {setAnchorEl(null);e.stopPropagation();}}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "notif-popover" }}
      >
        <div className="notif-popover__inner" role="menu" aria-label={t("Notifications")}>
          <Typography className="notif-head">{t("Notifications")}</Typography>

{/* ACTIONS SUMMARY (not readable/deleteable) */}
{actions.length > 0 && (
  <div className="notif-actionsbar" role="group" aria-label="Required actions">
    {(() => {
      const payNow        = actions.filter(a => a.cta === "PayNow").length;
      const shipNow       = actions.filter(a => a.cta === "ShipNow").length;
      const answerNow     = actions.filter(a => a.cta === "AnswerNow").length;     // trades
      const markReceived  = actions.filter(a => a.cta === "MarkReceived").length;  // trades

      const go = (preselected) => {
        const path = buildOffersPath({ preselected });   // you already have this helper
        // Close popover then navigate
        setAnchorEl(null);
        const current = location.pathname + location.search;
        if (current === path) {
          navigate("/refresh", { replace: true });
          setTimeout(() => navigate(path), 0);
        } else {
          navigate(path);
        }
      };

      return (
        <>
          {payNow > 0 && (
            <button className="na-chip na-pay" onClick={() => go("WaitingPayment")}>
              Pay now <b>{payNow}</b>
            </button>
          )}
          {shipNow > 0 && (
            <button className="na-chip na-ship" onClick={() => go("WaitingShipment")}>
              Ship now <b>{shipNow}</b>
            </button>
          )}
          {answerNow > 0 && (
            <button className="na-chip na-answer" onClick={() => go("Waiting")}>
              Answer trade <b>{answerNow}</b>
            </button>
          )}
          {markReceived > 0 && (
            <button className="na-chip na-received" onClick={() => go("Waiting")}>
              Mark received <b>{markReceived}</b>
            </button>
          )}
        </>
      );
    })()}
  </div>
)}


          <div className="notif-list">
            {items.map((n) => {
              const meta = parseMeta(n.meta);
              return (
                <div
                  key={n.id}
                  className={`notif-item ${n.isRead ? "is-read" : "is-unread"}`}
                  onClick={() => goTo(n)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === "Enter" ? goTo(n) : null)}
                >
                  {/* left: image + text */}
                  <div className="ni-main">
                    {n.image_url ? (
                      <img className="ni-img" src={n.image_url} alt="" loading="lazy" />
                    ) : (
                      <div className="ni-img ni-img--placeholder" />
                    )}

                    <div className="ni-text">
                      <div className="ni-title">
                        {!n.isRead && <span className="ni-dot" aria-hidden />}
                        {n.meta['cta']!=='View'?n.meta['cta']:humanTitle(n)}
                      </div>
                      <div className="ni-sub">
                        {n.body || humanBody(n)}
                      </div>
                    </div>
                  </div>

                  {/* time */}
                  <div className="ni-time" aria-hidden>
                    {new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>

                  {/* right: actions (stop propagation so click doesn't navigate) */}
                  <div className="ni-actions" onClick={(e) => e.stopPropagation()}>
                    {!n.isRead && (
                      <button
                        className="ni-iconbtn"
                        aria-label={t("Mark as read")}
                        title={t("Mark as read")}
                        onClick={() => markRead(n.id)}
                      >
                        {/* check-circle */}
                        <svg viewBox="0 0 24 24" className="ni-ico" aria-hidden="true">
                          <path d="M12 2a10 10 0 1 0 .001 20.001A10 10 0 0 0 12 2Zm-1 14-4-4 1.414-1.414L11 12.172l5.586-5.586L18 8l-7 8Z"/>
                        </svg>
                      </button>
                    )}
                    <button
                      className="ni-iconbtn ni-iconbtn--danger"
                      aria-label={t("Delete")}
                      title={t("Delete")}
                      onClick={() => deleteOne(n.id)}
                    >
                      {/* trash */}
                      <svg viewBox="0 0 24 24" className="ni-ico" aria-hidden="true">
                        <path d="M9 3h6l1 2h4v2H4V5h4l1-2Zm1 6h2v8h-2V9Zm4 0h2v8h-2V9ZM6 9h2v8H6V9Zm-1 12h14a2 2 0 0 0 2-2V7H3v12a2 2 0 0 0 2 2Z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Load more */}
          {cursor && items.length>5 && (
            <button className="notif-more" onClick={(e) => {loadList(cursor);e.stopPropagation();}}>
              {t("Load more") || "Load more"}
            </button>
          )}

          {/* Empty state */}
          {empty && <div className="notif-empty">{t("You’re all caught up!")}</div>}
        </div>
      </Popover>
    </>
  );
}

/* ---- Humanizers ---- */
function humanTitle(n) {
  switch (n.type) {
    case "trade_offer": return "New trade offer";
    case "sale_offer": return "New offer to buy";
    case "card_sold": return "Card sold";
    case "transaction_status_changed":
      return n.status_to ? `${n.status_to}` : "Status updated";
    case "message": return "Message";
    default: return "Notification";
  }
}
function humanBody(n) {
  if (n.type === "transaction_status_changed" && n.status_from && n.status_to) {
    return `Changed from ${n.status_from} to ${n.status_to}`;
  }
  return n.body || "";
}

